import { FC, useContext, useEffect } from "react";

import { FormType, formatCurrencyAmount, formatDateTimeToLocaleString, getDollarValue } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { FrontendFormContext } from "../../../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../../../common/contexts/LocaleContext";
import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "../../contexts/FormV2Context";
import { sendEmbedThankYouAnimationShownMessage } from "../../helpers/dynamicEmbedMessaging";

import { ThankYouLoader } from "@/canvas/features/ThankYouLoader/ThankYouLoader";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useTicketDrawer } from "@/components/TicketingDrawers/useTicketDrawer";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

const EVENT_DATE_FORMAT = {
  month: "long",
  day: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
} as const;

export const TicketingThankYouLoader: FC = () => {
  const { form, isEmbed, formCurrency } = useFormV2Context();
  const ticketingForm = form as UseFormReturn<TicketingPaymentInput>;
  const { organization, generateETicket, selectedPaymentMethod, displayedFormAmount } = useContext(FrontendFormContext);
  const {
    hasADate,
    isAuction,
    formObject: ticketing,
    localeField: ticketingField,
    selectedOccurrenceWithRates,
    ticketsPurchased,
  } = useTicketingFormV2ContextOrDefaults();
  const { locale, isoLocale } = useLocaleContext();
  const { displayGenerateReceipt: generateTaxReceipt, eligibleAmountTotal } = useTicketDrawer({
    form: ticketingForm,
  });
  const hasNoETickets = !ticketsPurchased?.some((ticket) => (ticket.purchasedNumber ?? 0) > 0);

  const startUtc = selectedOccurrenceWithRates?.occurrence?.startUtc;

  // Notify embed of thank you animation
  useEffect(() => {
    if (isEmbed) {
      sendEmbedThankYouAnimationShownMessage();
    }
  }, [isEmbed]);

  return (
    <ThankYouLoader
      organizationName={organization.name}
      formType={FormType.Ticketing}
      generateTaxReceipt={generateTaxReceipt}
      generateTicket={generateETicket && !hasNoETickets}
      isFree={displayedFormAmount === 0}
      hasADate={hasADate}
      paymentMethod={selectedPaymentMethod}
      isAuction={isAuction}
      isEntry={isSweepstakesCategory(ticketing.formCategory)}
      donorFirstName={ticketingForm.watch("firstName")}
      donorEmail={ticketingForm.getValues("email")}
      amount={
        displayedFormAmount > 0
          ? formatCurrencyAmount({
              value: getDollarValue(displayedFormAmount),
              locale: isoLocale,
              currency: formCurrency,
            })
          : undefined
      }
      taxReceiptAmount={
        eligibleAmountTotal > 0
          ? formatCurrencyAmount({
              value: getDollarValue(eligibleAmountTotal),
              locale: isoLocale,
              currency: formCurrency,
            })
          : undefined
      }
      eventName={ticketingField?.title}
      eventDate={
        startUtc
          ? formatDateTimeToLocaleString(startUtc, locale, EVENT_DATE_FORMAT, ticketing?.eventTimezone)
          : undefined
      }
    />
  );
};

export const DonationFormThankYouLoader: FC = () => {
  const { formObject, displayGenerateReceipt, isEmbed, form, formCurrency } = useFormV2Context(FormType.DonationForm);
  const { organization } = formObject;
  const { selectedPaymentMethod, displayedFormAmount } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();

  // Notify embed of thank you animation
  useEffect(() => {
    if (isEmbed) {
      sendEmbedThankYouAnimationShownMessage();
    }
  }, [isEmbed]);

  return (
    <ThankYouLoader
      organizationName={organization!.name}
      formType={FormType.DonationForm}
      generateTaxReceipt={displayGenerateReceipt}
      generateTicket={false}
      isFree={false}
      hasADate={false}
      paymentMethod={selectedPaymentMethod}
      isAuction={false}
      donorFirstName={form.watch("firstName")}
      donorEmail={form.getValues("email")}
      amount={
        displayedFormAmount > 0
          ? formatCurrencyAmount({
              value: getDollarValue(displayedFormAmount),
              locale: isoLocale,
              currency: formCurrency,
            })
          : undefined
      }
      taxReceiptAmount={
        displayedFormAmount > 0
          ? formatCurrencyAmount({
              value: getDollarValue(displayedFormAmount),
              locale: isoLocale,
              currency: formCurrency,
            })
          : undefined
      }
    />
  );
};
